import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { Download, ArrowRight, ArrowLeft, FileSpreadsheet, FileText, Building2, MapPin } from 'lucide-react';

export function FMBExportDialog({ open, onOpenChange, fmbStatus }) {
  const { api } = useAuth();
  const [step, setStep] = useState(1);
  const [groupBy, setGroupBy] = useState('mohalla');
  const [mohallaSelection, setMohallaSelection] = useState('all');
  const [selectedMohallas, setSelectedMohallas] = useState([]);
  const [sheetType, setSheetType] = useState('combined');
  const [format, setFormat] = useState('excel');
  const [exporting, setExporting] = useState(false);
  const [mohallas, setMohallas] = useState([]);

  useEffect(() => {
    if (open) {
      fetchMohallas();
      // Reset state when dialog opens
      setStep(1);
      setGroupBy('mohalla');
      setMohallaSelection('all');
      setSelectedMohallas([]);
      setSheetType('combined');
      setFormat('excel');
    }
  }, [open]);

  const fetchMohallas = async () => {
    try {
      const response = await api.get('/mohallas');
      setMohallas(response.data.mohallas || []);
    } catch (error) {
      console.error('Failed to fetch mohallas');
    }
  };

  const handleMohallaToggle = (mohallaName) => {
    setSelectedMohallas(prev => 
      prev.includes(mohallaName) 
        ? prev.filter(m => m !== mohallaName)
        : [...prev, mohallaName]
    );
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      
      const mohallaParam = mohallaSelection === 'all' 
        ? 'all' 
        : selectedMohallas.join(',');
      
      if (mohallaSelection === 'selected' && selectedMohallas.length === 0) {
        toast.error('Please select at least one mohalla');
        return;
      }
      
      const url = `/export/fmb?fmb_status=${encodeURIComponent(fmbStatus)}&group_by=${groupBy}&mohallas=${encodeURIComponent(mohallaParam)}&sheet_type=${sheetType}&format=${format}`;
      
      const response = await api.get(url, { responseType: 'blob' });
      
      // Create blob
      const mimeType = format === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mimeType });
      const fileName = `FMB_${fmbStatus.replace(/ /g, '_')}_${groupBy}_${mohallaSelection}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      // Mobile-friendly download
      if (navigator.userAgent.match(/iPhone|iPad|iPod|Android/i)) {
        const blobUrl = window.URL.createObjectURL(blob);
        const newWindow = window.open(blobUrl, '_blank');
        if (!newWindow) {
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
      } else {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
        }, 100);
      }
      
      toast.success(`${format.toUpperCase()} file downloaded successfully`);
      onOpenChange(false);
    } catch (error) {
      console.error('Export error:', error);
      if (error.response?.status === 404) {
        toast.error('No data found for the selected criteria');
      } else {
        toast.error('Failed to export: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setExporting(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return true;
    if (step === 2) {
      if (mohallaSelection === 'selected' && selectedMohallas.length === 0) return false;
      return true;
    }
    if (step === 3) return true;
    if (step === 4) return true;
    return true;
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 font-medium">Step 1: Choose grouping method</p>
            <RadioGroup value={groupBy} onValueChange={setGroupBy} className="space-y-3">
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => setGroupBy('mohalla')}>
                <RadioGroupItem value="mohalla" id="mohalla" />
                <Label htmlFor="mohalla" className="flex items-center gap-2 cursor-pointer flex-1">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium">Download Mohalla-wise</p>
                    <p className="text-xs text-slate-500">Group data by Mohalla</p>
                  </div>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => setGroupBy('sector')}>
                <RadioGroupItem value="sector" id="sector" />
                <Label htmlFor="sector" className="flex items-center gap-2 cursor-pointer flex-1">
                  <MapPin className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-medium">Download Sector-wise</p>
                    <p className="text-xs text-slate-500">Group data by Sector</p>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 font-medium">Step 2: Select Mohallas</p>
            <RadioGroup value={mohallaSelection} onValueChange={setMohallaSelection} className="space-y-3">
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => setMohallaSelection('all')}>
                <RadioGroupItem value="all" id="all-mohallas" />
                <Label htmlFor="all-mohallas" className="cursor-pointer flex-1">
                  <p className="font-medium">Download ALL Mohallas</p>
                  <p className="text-xs text-slate-500">Include data from all {mohallas.length} mohallas</p>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => setMohallaSelection('selected')}>
                <RadioGroupItem value="selected" id="selected-mohallas" />
                <Label htmlFor="selected-mohallas" className="cursor-pointer flex-1">
                  <p className="font-medium">Download Selected Mohalla(s)</p>
                  <p className="text-xs text-slate-500">Choose specific mohallas</p>
                </Label>
              </div>
            </RadioGroup>
            
            {mohallaSelection === 'selected' && (
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2 bg-slate-50">
                {mohallas.map(mohalla => (
                  <div key={mohalla.id} className="flex items-center space-x-2">
                    <Checkbox 
                      id={mohalla.id}
                      checked={selectedMohallas.includes(mohalla.name)}
                      onCheckedChange={() => handleMohallaToggle(mohalla.name)}
                    />
                    <Label htmlFor={mohalla.id} className="cursor-pointer text-sm">
                      {mohalla.name}
                    </Label>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 font-medium">Step 3: Sheet structure</p>
            <RadioGroup value={sheetType} onValueChange={setSheetType} className="space-y-3">
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => setSheetType('combined')}>
                <RadioGroupItem value="combined" id="combined" />
                <Label htmlFor="combined" className="cursor-pointer flex-1">
                  <p className="font-medium">Single Combined Sheet</p>
                  <p className="text-xs text-slate-500">All data in one sheet/page</p>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => setSheetType('separate')}>
                <RadioGroupItem value="separate" id="separate" />
                <Label htmlFor="separate" className="cursor-pointer flex-1">
                  <p className="font-medium">Separate Sheet for EACH {groupBy === 'mohalla' ? 'Mohalla' : 'Sector'}</p>
                  <p className="text-xs text-slate-500">One sheet/page per {groupBy}</p>
                </Label>
              </div>
            </RadioGroup>
          </div>
        );
      
      case 4:
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 font-medium">Step 4: Choose format</p>
            <div className="grid grid-cols-2 gap-4">
              <Button 
                variant={format === 'excel' ? 'default' : 'outline'}
                className={`h-24 flex flex-col items-center justify-center gap-2 ${format === 'excel' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                onClick={() => setFormat('excel')}
              >
                <FileSpreadsheet className="w-8 h-8" />
                <span>Excel (.xlsx)</span>
              </Button>
              <Button 
                variant={format === 'pdf' ? 'default' : 'outline'}
                className={`h-24 flex flex-col items-center justify-center gap-2 ${format === 'pdf' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                onClick={() => setFormat('pdf')}
              >
                <FileText className="w-8 h-8" />
                <span>PDF</span>
              </Button>
            </div>
            
            {/* Summary */}
            <div className="bg-slate-100 rounded-lg p-4 space-y-2">
              <p className="font-medium text-sm">Export Summary:</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>• Status: <strong>FMB {fmbStatus}</strong></li>
                <li>• Grouping: <strong>{groupBy === 'mohalla' ? 'Mohalla-wise' : 'Sector-wise'}</strong></li>
                <li>• Mohallas: <strong>{mohallaSelection === 'all' ? 'All' : `${selectedMohallas.length} selected`}</strong></li>
                <li>• Sheets: <strong>{sheetType === 'combined' ? 'Single combined' : `Separate per ${groupBy}`}</strong></li>
                <li>• Format: <strong>{format.toUpperCase()}</strong></li>
                <li>• Data: <strong>HOF Only (no family members)</strong></li>
              </ul>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Export FMB {fmbStatus} List
          </DialogTitle>
        </DialogHeader>
        
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 py-2">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s === step ? 'bg-navy text-white' : s < step ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {s}
              </div>
              {s < 4 && <div className={`w-8 h-0.5 ${s < step ? 'bg-green-500' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>
        
        <div className="py-4">
          {renderStep()}
        </div>
        
        <DialogFooter className="flex justify-between">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(s => s - 1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {step < 4 ? (
              <Button onClick={() => setStep(s => s + 1)} disabled={!canProceed()} className="bg-navy hover:bg-navy/90">
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleExport} disabled={exporting || !canProceed()} className="bg-green-600 hover:bg-green-700">
                {exporting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download {format.toUpperCase()}
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
